<template>
  <article class="card-custom act">
    <div class="act-head">
      <div>
        <div class="act-eyebrow">Detalle por cuenta</div>
        <div class="act-title">Evolución y ranking de cuentas</div>
        <div class="act-sub">
          Cuentas con seguimiento prioritario · {{ etiquetaPeriodo }} ·
          {{ filas.length }} cuentas · Fuente: encuestas ESAC
        </div>
      </div>
      <input
        v-model="busqueda"
        class="act-search"
        type="search"
        placeholder="Buscar cuenta…"
        aria-label="Buscar cuenta"
      />
    </div>

    <div class="act-scroll">
      <table class="act-table">
        <thead>
          <tr>
            <th class="sticky-col">Cuenta</th>
            <th>Estado</th>
            <th v-for="a in anios" :key="a" class="num">{{ a }}</th>
            <th class="num">Variación</th>
            <th>Tendencia</th>
            <th class="num">Encuestas</th>
            <th>Última eval.</th>
            <th>Detalle</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="f in visibles" :key="f.idCuenta">
            <td class="sticky-col act-name" :title="f.cliente">
              {{ f.cliente }}
            </td>
            <td>
              <span class="act-badge" :style="{ background: f.color }">
                {{ etiquetaEstado(f.semaforo) }}
              </span>
            </td>
            <td v-for="p in f.porAnio" :key="p.anio" class="num">
              <template v-if="p.score != null">
                <span class="act-score">{{ p.score.toFixed(1) }}</span>
                <span class="act-n">({{ p.encuestas }})</span>
              </template>
              <span v-else class="act-nd">—</span>
            </td>
            <td class="num">
              <span
                v-if="f.variacion != null"
                :class="f.variacion >= 0 ? 'up' : 'down'"
              >
                {{ f.variacion >= 0 ? "▲" : "▼" }}
                {{ Math.abs(f.variacion).toFixed(1) }}
              </span>
              <span v-else class="act-nd">—</span>
            </td>
            <td>
              <MiniTrend :valores="f.porAnio.map((p) => p.score)" />
            </td>
            <td class="num">{{ f.encuestas }}</td>
            <td class="act-fecha">{{ f.ultimaEval }}</td>
            <td>
              <!-- Las cuentas sin ninguna evaluación no tienen nada que
                   desglosar: el panel quedaría vacío en todas sus pestañas. -->
              <button
                v-if="f.evaluada"
                class="act-detalle"
                @click="$emit('ver-detalle', f)"
              >
                Ver detalle <i class="bi bi-chevron-right"></i>
              </button>
              <span v-else class="act-nd">—</span>
            </td>
          </tr>
          <tr v-if="!visibles.length">
            <td :colspan="anios.length + 7" class="act-empty">
              Ninguna cuenta coincide con los filtros aplicados.
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <button v-if="hayMas" class="act-more" @click="limite += 20">
      Mostrar más ({{ filtradas.length - visibles.length }} restantes)
    </button>

    <p class="act-foot">
      El número entre paréntesis es la cantidad de encuestas de ese año. La
      variación compara el primer y el último año con dato dentro del periodo
      seleccionado. El sparkline usa escala propia por cuenta: comunica la forma
      de la tendencia, no el nivel — ese está en las columnas de año.
    </p>
  </article>
</template>

<script>
const ETIQUETAS = {
  VERDE: "Favorable",
  AMARILLO: "Seguimiento",
  ROJO: "Crítico",
  SIN_DATO: "Sin evaluar",
};

import MiniTrend from "./MiniTrend.vue";

export default {
  name: "AccountComparisonTable",
  components: { MiniTrend },

  props: {
    // Salida de comparativoCuentas()
    filas: { type: Array, default: () => [] },
    anios: { type: Array, default: () => [] },
  },

  emits: ["ver-detalle"],

  data() {
    return {
      busqueda: "",
      limite: 20,
    };
  },

  computed: {
    // Refleja la selección de la barra de filtros. Los años elegidos pueden no
    // ser contiguos (p. ej. 2024 y 2026), así que se listan en vez de usar un
    // guion de rango que sugeriría que 2025 está incluido.
    etiquetaPeriodo() {
      if (!this.anios.length) return "Sin periodo";
      if (this.anios.length === 1) return `Año ${this.anios[0]}`;
      return `Tendencia ${this.anios.join(" · ")}`;
    },

    filtradas() {
      const q = this.busqueda.trim().toLowerCase();
      if (!q) return this.filas;
      return this.filas.filter((f) => f.cliente.toLowerCase().includes(q));
    },

    visibles() {
      return this.filtradas.slice(0, this.limite);
    },

    hayMas() {
      return this.filtradas.length > this.visibles.length;
    },
  },

  watch: {
    // Al cambiar los filtros o la búsqueda se vuelve a la primera página.
    filas() {
      this.limite = 20;
    },
    busqueda() {
      this.limite = 20;
    },
  },

  methods: {
    etiquetaEstado(s) {
      return ETIQUETAS[s] || s;
    },
  },
};
</script>

<style scoped>
.act-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
  flex-wrap: wrap;
  margin-bottom: 14px;
}

.act-eyebrow {
  font-size: 10px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  font-weight: 700;
  color: var(--brand-text);
  margin-bottom: 2px;
}

.act-title {
  font-size: 16px;
  font-weight: 700;
  color: var(--heading);
}

.act-sub {
  font-size: 12px;
  color: var(--txt-dim);
}

.act-search {
  border: 1px solid var(--border);
  background: var(--white);
  color: var(--txt);
  border-radius: 10px;
  padding: 7px 12px;
  font-size: 12.5px;
  font-family: inherit;
  min-width: 180px;
}
.act-search:focus {
  outline: none;
  border-color: var(--primary);
}

/* ---------- Tabla ---------- */
.act-scroll {
  overflow-x: auto;
  margin: 0 -4px;
}

.act-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12.5px;
  white-space: nowrap;
}

.act-table th {
  text-align: left;
  font-size: 10.5px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--txt-faint);
  font-weight: 700;
  padding: 8px 10px;
  border-bottom: 1px solid var(--border-2);
}

.act-table td {
  padding: 9px 10px;
  border-bottom: 1px solid var(--border);
}

.act-table tbody tr:hover td {
  background: var(--surface);
}

.num {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

/* La primera columna se mantiene visible al desplazar en horizontal. */
.sticky-col {
  position: sticky;
  left: 0;
  background: var(--white);
  z-index: 1;
}
.act-table tbody tr:hover .sticky-col {
  background: var(--gray-100);
}

.act-name {
  font-weight: 600;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.act-badge {
  display: inline-block;
  color: #fff;
  border-radius: 999px;
  padding: 2px 9px;
  font-size: 10.5px;
  font-weight: 700;
}

.act-score {
  font-weight: 700;
}

.act-n {
  color: var(--txt-faint);
  font-size: 11px;
  margin-left: 3px;
}

.act-nd {
  color: var(--txt-faint);
}

.up {
  color: var(--verde);
  font-weight: 700;
}
.down {
  color: var(--rojo);
  font-weight: 700;
}

.act-fecha {
  color: var(--txt-dim);
  font-size: 11.5px;
}

.act-detalle {
  border: 1px solid var(--border);
  background: transparent;
  color: var(--brand-text);
  border-radius: 8px;
  padding: 4px 10px;
  font-size: 11.5px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  transition: all 0.18s ease;
}
.act-detalle:hover {
  border-color: var(--primary);
  background: var(--surface);
}

.act-empty {
  text-align: center;
  padding: 20px;
  color: var(--txt-dim);
}

.act-more {
  margin-top: 10px;
  width: 100%;
  border: 1px dashed var(--border-2);
  background: transparent;
  color: var(--txt-dim);
  border-radius: 10px;
  padding: 8px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}
.act-more:hover {
  border-color: var(--primary);
  color: var(--primary);
}

.act-foot {
  margin: 10px 0 0;
  font-size: 10.5px;
  color: var(--txt-faint);
  line-height: 1.45;
}
</style>
